import APP_CONSTANTS from '@/config/constants';
import { databaseService } from '@/services/databaseService';

interface SnapshotAwareChecklist {
  id: string;
  empresa_id?: string | null;
  snapshot_id?: string | null;
}

interface SnapshotRecord {
  id: string;
  empresa_id: string;
  checklist_id: string;
}

interface SnapshotFieldRecord {
  id: string;
  empresa_id: string;
  snapshot_id: string;
}

const pendingSnapshotIds = new Map<string, string>();

export const markChecklistSnapshotPending = (checklistId: string, snapshotId: string): void => {
  const normalizedChecklistId = checklistId.trim();
  const normalizedSnapshotId = snapshotId.trim();
  if (!normalizedChecklistId || !normalizedSnapshotId) return;
  pendingSnapshotIds.set(normalizedChecklistId, normalizedSnapshotId);
};

export const clearChecklistSnapshotPending = (checklistId: string): void => {
  pendingSnapshotIds.delete(checklistId);
};

export const filterChecklistsWithReadySnapshots = async <T extends SnapshotAwareChecklist>(
  checklists: T[],
): Promise<T[]> => {
  const readiness = await Promise.all(checklists.map(async checklist => {
    const pendingSnapshotId = pendingSnapshotIds.get(checklist.id);
    if (!pendingSnapshotId) return true;

    const companyId = String(checklist.empresa_id ?? '').trim();
    if (!companyId) return false;

    const snapshot = await databaseService.readItem<SnapshotRecord>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME,
      pendingSnapshotId,
    );
    if (
      !snapshot
      || snapshot.empresa_id !== companyId
      || snapshot.checklist_id !== checklist.id
    ) {
      return false;
    }

    const fields = await databaseService.listItems<SnapshotFieldRecord>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      { empresa_id: companyId, snapshot_id: pendingSnapshotId },
    );
    const ready = fields.some(field => (
      field.empresa_id === companyId && field.snapshot_id === pendingSnapshotId
    ));
    if (ready) clearChecklistSnapshotPending(checklist.id);
    return ready;
  }));

  return checklists.filter((_checklist, index) => readiness[index]);
};

export const __checklistSnapshotVisibilityTestHooks = {
  clearAll() {
    pendingSnapshotIds.clear();
  },
  isPending(checklistId: string) {
    return pendingSnapshotIds.has(checklistId);
  },
};
